/**
 * What kind of source an answer's `sourceSlices` entry describes, and the render-ready foundation rows.
 *
 * `source_slices` is heterogeneous: a document slice carries `fileinfoId`, a round answer carries
 * `questionId`, and a Foundation Data v2 figure carries `foundationDatapointUid`. `ActivityDetailsSources`
 * knew the first two and dropped the third silently, so an answer built only on the organisation's approved
 * figures rendered no Sources section at all — it read as though the AI had used nothing, on exactly the
 * answers whose provenance matters most.
 *
 * Its own module so vitest can cover it: the repo has no component-render harness (empty `setupFiles`, zero
 * render tests), so classification logic that lives inside a component is untestable logic.
 */

import { foundationNumber } from "../../../utils/foundationCitation";

/**
 * The `/sourceSlices` json-patch value off an activity, always an array.
 *
 * @param {object|null|undefined} activity IssbActivityDto
 * @returns {Array<object>}
 */
export function sourceSlicesOf(activity) {
  const value = activity?.jsonPatch?.find((patch) => patch?.path === "/sourceSlices")?.value;
  return Array.isArray(value) ? value : [];
}

/**
 * Which kind of source one slice is — exclusive, in this precedence.
 *
 * **`"foundation"` is the leftover, deliberately.** A slice is foundation only when it carries a
 * `foundationDatapointUid` and neither of the other two discriminators, so adding this kind cannot move or
 * duplicate anything that renders today: the file and Q&A filters in `ActivityDetailsSources` are unchanged,
 * and a hypothetical hybrid entry keeps the row it already got.
 *
 * @param {object|null|undefined} slice
 * @returns {"file"|"qa"|"foundation"|"unknown"}
 */
export function sourceSliceKind(slice) {
  if (slice?.fileinfoId) return "file";
  if (slice?.questionId) return "qa";
  if (slice?.foundationDatapointUid) return "foundation";
  return "unknown";
}

/**
 * The foundation citations on an answer, ready to render.
 *
 * The label comes off the slice — `low_calorie_slice` keeps `dataKey`, so naming the figure needs no request.
 * Only its VALUE requires one, which is why the panel fetches and this does not.
 *
 * @param {Array<object>|null|undefined} slices
 * @returns {Array<{key: string, label: string, year: number|null, version: number|null,
 *                  datapointUid: string, curatedPointId: *, activationId: string}>}
 */
export function foundationSourceRows(slices) {
  const rows = [];
  const seen = new Set();
  for (const slice of slices || []) {
    if (sourceSliceKind(slice) !== "foundation") continue;
    const version = foundationNumber(slice.foundationVersion);
    const key = slice.id || `${slice.foundationDatapointUid}:${version ?? "?"}`;
    if (seen.has(key)) continue; // two selected years can cite one datapoint twice — one row, one React key
    seen.add(key);
    rows.push({
      key: String(key),
      // dataKey is the metric name; title reads "Foundation Data 2025", which is a usable second best.
      label: slice.dataKey || slice.title || "Approved figure",
      year: foundationNumber(slice.foundationYear),
      version,
      datapointUid: slice.foundationDatapointUid,
      curatedPointId: slice.curatedPointId,
      // Per (datapoint, version), not per datapoint: `doOpen` uses this id to decide whether the sidebar is
      // already showing this thing, so two citations of one datapoint at different versions must differ or
      // clicking the second leaves the first panel up.
      activationId: `CitationSidePanel:foundation:${slice.foundationDatapointUid}:${version ?? "?"}`,
    });
  }
  return rows;
}
